import { useStore } from '@nanostores/react';
import { useCallback } from 'react';
import { checkAndUpdateDeploymentStatus } from '~/lib/utils/netlify-helpers';
import {
  netlifyStore,
  setDeploymentStatus,
  setDeployedUrl,
  setError,
  setAdminUrl,
  setIsCheckingStatus,
} from '~/lib/stores/netlify';
import type { UseNetlifyStatusReturn, DeploymentStatus } from '~/lib/types/netlify';

export function useNetlifyStatus(
  deployedId: string | null,
  accessToken: string | null | undefined,
): UseNetlifyStatusReturn {
  // Use the global store for all shared state
  const store = useStore(netlifyStore);
  const { deploymentStatus, deployedUrl, error } = store;

  // Function to update deployment status
  const updateDeploymentStatus = useCallback(async () => {
    console.log('[useNetlifyStatus] Updating deployment status', {
      accessToken: !!accessToken,
      deployedId,
    });

    if (!accessToken || !deployedId) {
      console.warn('[useNetlifyStatus] Cannot check status - missing', !accessToken ? 'accessToken' : 'deployedId');

      return;
    }

    try {
      await checkAndUpdateDeploymentStatus(
        accessToken,
        deployedId,
        setDeploymentStatus,
        setDeployedUrl,
        setAdminUrl,
        setIsCheckingStatus,
        setError,
      );
    } catch (error) {
      console.error('[useNetlifyStatus] Error updating deployment status:', error);
    }
  }, [accessToken, deployedId]);

  // Helper function to get current step for UI
  const getCurrentStep = useCallback((): number => {
    if (!accessToken) {
      return 0;
    }

    if (deploymentStatus === 'new') {
      return 2;
    }

    if (deploymentStatus === 'building') {
      return 3;
    }

    if (deploymentStatus === 'ready') {
      return 4;
    }

    return 0;
  }, [accessToken, deploymentStatus]);

  return {
    deploymentStatus,
    deployedUrl,
    error,
    updateDeploymentStatus,
    getCurrentStep,
  };
}
